import { Component, h, useEffect, useRef, useState } from '@lukekaalim/act';
import { html } from '@lukekaalim/act-web';
import { Login } from './LogIn';
import { SignUp } from './SignUp';

import classes from './index.module.css';
import { Animation, lerp, ModalComponent, useAnimation } from '@kosmod/kit';
import { createListAnimationHook, easeOutQuad } from '@kosmod/kit/list';
import { SignOut } from './SignOut';
import { useFrontendCore, useFrontendServices } from '@kosmod/core';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { AuthState } from '../../hooks';

export * from './LogIn';
export * from './SignUp';

export * from './input';


const usePageAnim = createListAnimationHook<('login' | 'sign-up' | 'sign-out')>(p => p, 400);
const useModalAnim = createListAnimationHook<('login' | 'sign-up' | 'sign-out')>(p => p, 400, () => {
  const now = performance.now();
  return Animation(now, 400, [0, 1], ([a, b], t) => lerp(a, b, easeOutQuad(t)))
});

export type AuthenticatePageProps = {
  mode: 'login' | 'sign-up' | 'sign-out',
  destination?: string | null,
  auth: AuthState,
}

export const AuthenticatePage: Component<AuthenticatePageProps> = ({ mode, destination, auth }) => {
  const { site } = useFrontendServices();
  
  const pathAnim = usePageAnim(auth.account ? ['sign-out'] : [mode] as const)

  const onLoginComplete = () => {
    site.navigate({ path: '/games' });
  }
  const onSignUpComplete = () => {
    site.navigate({ path: '/games' });
  };
  const onSignOut = () => {
    auth.logout();
  }
  const onClickLogin = () => {
    site.navigate({ path: '/login' });
  }
  const onClickSignUp = () => {
    site.navigate({ path: '/sign-up' });
  }
  const onClickLogo = () => {
    site.navigate({ path: '/' });
  }

  return [
    h(Header, { account: auth.account, auth }),
    h(html.div, { className: classes.authenticate }, [
      pathAnim.map(path => {
        switch (path.element) {
          case 'login':
            return h(PageAnimator, { key: path.key, anim: path.anim }, h(Login, { onLoginComplete, onClickSignUp, onClickLogo, auth }));
          case 'sign-up':
            return h(PageAnimator, { key: path.key, anim: path.anim }, h(SignUp, { onSignUpComplete, onClickLogin, auth }));
          case 'sign-out':
            if (!auth.account) {
              return h(PageAnimator, { key: path.key, anim: path.anim }, h(Login, { onLoginComplete, onClickLogo, onClickSignUp, auth }));
            }
            return h(PageAnimator, { key: path.key, anim: path.anim }, h(SignOut, { account: auth.account,  onSignOut, auth }));
          default:
            return null;
        }
      }),
    ]),
    h(Footer, { account: auth.account }),
  ]
}

const PageAnimator: Component<{ anim: Animation<number> }> = ({ anim, children }) => {
  const ref = useRef<HTMLElement | null>(null)

  useAnimation(anim, (value) => {
    const el = ref.get()!;

    if (value === 1) {
      el.style.display = 'block';
      el.style.transform = ``;
    } else {
      el.style.display = value > 0.5 && value < 1.5 ? 'block' : 'none';
      el.style.transform = `perspective(5000px) rotateY(${180 - ((2 - (value % 2)) * 180)}deg)`
    }
  })

  return h(html.div, { className: classes.front, ref }, children);
};

export const createAuthenticateModal = (initialMode: 'login' | 'sign-up' | 'sign-out', auth: AuthState): ModalComponent => ({ close }) => {
  const [mode, setMode] = useState(initialMode);
  
  const pathAnim = usePageAnim(auth.account ? ['sign-out'] : [mode] as const)

  const onLoginComplete = () => {
    close();
  }
  const onSignUpComplete = () => {
    close();
  };
  const onSignOut = () => {
    auth.logout();
    close();
  }
  const onClickSignUp = () => {
    setMode('sign-up');
  }
  const onClickLogin = () => {
    setMode('login')
  }
  const onClickLogo = () => {
    
  }

  return h(html.div, { className: classes.authenticate }, [
    pathAnim.map(path => {
      switch (path.element) {
        case 'login':
          return h(PageAnimator, { key: path.key, anim: path.anim },
            h(Login, { onLoginComplete, onClickSignUp, onClickLogo, auth }));

        case 'sign-up':
          return h(PageAnimator, { key: path.key, anim: path.anim },
            h(SignUp, { onSignUpComplete, onClickLogin, auth }));

        case 'sign-out':
          if (!auth.account) {
            return h(PageAnimator, { key: path.key, anim: path.anim },
              h(Login, { onLoginComplete, onClickLogo, onClickSignUp, auth }));
          }

          return h(PageAnimator, { key: path.key, anim: path.anim },
            h(SignOut, { account: auth.account,  onSignOut, auth }));
        default:
          return null;
      }
    }),
  ])
}